import { useState, useRef, useEffect, useCallback } from 'react'
import type { QuestionProps } from '../eform.types'
import { stripWhiteBg } from '../EFormRenderer'
import { stripHtmlToText } from '@/lib/utils'

export default function ScrollAcceptField({ question, value, onChange, error }: QuestionProps) {
  const scrollRef = useRef<HTMLDivElement>(null)
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false)
  const isAccepted = value === 'accepted'

  const checkLabel =
    stripHtmlToText(question.instructionText) ||
    'I have read and accept the above'

  const handleScroll = useCallback(() => {
    const el = scrollRef.current
    if (!el || hasScrolledToBottom) return
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 10) {
      setHasScrolledToBottom(true)
    }
  }, [hasScrolledToBottom])

  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    // Auto-enable if the content fits without scrolling
    if (el.scrollHeight <= el.clientHeight + 2) {
      setHasScrolledToBottom(true)
    }
  }, [])

  const handleToggle = () => {
    if (!hasScrolledToBottom) return
    onChange(isAccepted ? '' : 'accepted')
  }

  return (
    <div>
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className="max-h-72 overflow-y-auto rounded-lg border border-slate-200 p-4 prose prose-sm max-w-none text-slate-700 [&_*]:bg-transparent"
        dangerouslySetInnerHTML={{ __html: stripWhiteBg(question.questionContent) }}
      />

      {!hasScrolledToBottom && (
        <p className="text-xs text-slate-400 mt-1.5 text-center italic">
          Scroll to the bottom to enable acceptance
        </p>
      )}

      <label
        className={`flex items-start gap-2.5 mt-3 select-none ${
          hasScrolledToBottom ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'
        }`}
      >
        <input
          type="checkbox"
          checked={isAccepted}
          onChange={handleToggle}
          disabled={!hasScrolledToBottom}
          className="mt-0.5 h-4 w-4 rounded border-slate-300 accent-blue-600 shrink-0"
        />
        <span className="text-sm text-slate-700">{checkLabel}</span>
      </label>

      {error && (
        <p className="text-xs text-red-500 mt-1">{error}</p>
      )}
    </div>
  )
}
